import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import { SwalAlert } from '@/hooks/useSweetAlert';
import type {
  ChatMessage,
  ChatChannel,
  ChatMember,
  ChatRole,
  ReactionEmoji,
  ChatReaction,
} from '@/mocks/adminCommunityChat';
import {
  channelCategories,
  chatMessages,
  chatMembers,
  memberRoleLabels,
  memberRoleColors,
} from '@/mocks/adminCommunityChat';

/* ─── Constantes ─── */
const REACTION_OPTIONS: ReactionEmoji[] = ['👍', '❤️', '😂', '🔥', '👏', '😮', '😢', '🙌'];

const EMOJI_LIST = ['😀','😂','❤️','🔥','👍','👏','🎉','😍','🤩','😎','🥳','😢','😡','💯','✨','🙌','🤔','😴','🎵','📢'];

/* ════════════════════════════════════════════
   COMPONENTES INTERNOS
   ════════════════════════════════════════════ */

/* ─── Avatar ─── */
function UserAvatar({ name, size }: { name: string; size?: 'sm' | 'md' | 'lg' }) {
  const sizeMap = { sm: 'w-7 h-7 text-[10px]', md: 'w-9 h-9 text-xs', lg: 'w-10 h-10 text-sm' };
  const cls = sizeMap[size || 'md'];
  const initial = name.charAt(0).toUpperCase();
  return (
    <div className={`${cls} rounded-full bg-foreground-300 flex items-center justify-center text-foreground-50 font-bold shrink-0`}>
      {initial}
    </div>
  );
}

/* ─── Role Badge ─── */
function RoleBadge({ role }: { role: ChatRole }) {
  if (role === 'listener') return null;
  return (
    <span className={`text-[10px] font-semibold ${memberRoleColors[role]}`}>
      {memberRoleLabels[role]}
    </span>
  );
}

/* ─── Online Dot ─── */
function OnlineDot({ isOnline }: { isOnline: boolean }) {
  return (
    <span className={`absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2 border-background-900 shrink-0 ${isOnline ? 'bg-emerald-500' : 'bg-foreground-500'}`}></span>
  );
}

/* ─── Reaction Picker ─── */
function ReactionPicker({ onSelect, onClose }: { onSelect: (emoji: ReactionEmoji) => void; onClose: () => void }) {
  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose}></div>
      <div className="absolute bottom-full left-0 mb-1.5 z-50 bg-background-800 border border-background-700 rounded-xl p-1.5 flex items-center gap-0.5 shadow-lg">
        {REACTION_OPTIONS.map((emoji) => (
          <button
            key={emoji}
            onClick={() => onSelect(emoji)}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-700 text-sm cursor-pointer transition-colors"
          >
            {emoji}
          </button>
        ))}
      </div>
    </>
  );
}

/* ─── Emoji Picker Popover ─── */
function EmojiPickerPopover({ onSelect, onClose }: { onSelect: (emoji: string) => void; onClose: () => void }) {
  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose}></div>
      <div className="absolute bottom-full right-0 mb-2 z-50 bg-background-800 border border-background-700 rounded-xl p-2 shadow-lg w-[220px]">
        <div className="grid grid-cols-8 gap-1">
          {EMOJI_LIST.map((emoji) => (
            <button
              key={emoji}
              onClick={() => onSelect(emoji)}
              className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-background-700 text-sm cursor-pointer transition-colors"
            >
              {emoji}
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

/* ─── Reaction Bar ─── */
function ReactionBar({ reactions, onAddReaction, onRemoveReaction }: {
  reactions: ChatReaction[];
  onAddReaction: (emoji: ReactionEmoji) => void;
  onRemoveReaction: (emoji: ReactionEmoji) => void;
}) {
  if (reactions.length === 0) return null;
  return (
    <div className="flex items-center gap-1 mt-1 flex-wrap">
      {reactions.map((r) => (
        <button
          key={r.emoji}
          onClick={() => r.reactedByMe ? onRemoveReaction(r.emoji) : onAddReaction(r.emoji)}
          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-xs cursor-pointer transition-colors whitespace-nowrap ${
            r.reactedByMe
              ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30'
              : 'bg-background-800 text-foreground-300 hover:bg-background-700 border border-background-700'
          }`}
          title={r.users.join(', ')}
        >
          <span className="text-[13px] leading-none">{r.emoji}</span>
          <span className="text-[11px]">{r.count}</span>
        </button>
      ))}
    </div>
  );
}

/* ─── Message Bubble ─── */
function MessageBubble({
  msg,
  isFirstInGroup,
  onReply,
  onReact,
  onPin,
  onDelete,
  onBlock,
  onHighlight,
}: {
  msg: ChatMessage;
  isFirstInGroup: boolean;
  onReply: (msg: ChatMessage) => void;
  onReact: (msgId: number, emoji: ReactionEmoji) => void;
  onPin: (msg: ChatMessage) => void;
  onDelete: (msg: ChatMessage) => void;
  onBlock: (msg: ChatMessage) => void;
  onHighlight: (msg: ChatMessage) => void;
}) {
  const [showActions, setShowActions] = useState(false);
  const [showReactionPicker, setShowReactionPicker] = useState(false);

  const isPinned = msg.isPinned;
  const isHighlighted = msg.isHighlighted;

  return (
    <div
      className={`group relative px-4 py-0.5 hover:bg-background-800/50 transition-colors ${
        isHighlighted ? 'bg-amber-500/5 border-l-2 border-amber-500/50' : ''
      } ${isPinned ? 'bg-primary-500/3 border-l-2 border-primary-500/40' : ''}`}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => { setShowActions(false); setShowReactionPicker(false); }}
    >
      <div className="flex items-start gap-3 pt-1.5">
        {isFirstInGroup ? (
          <div className="relative shrink-0 mt-0.5">
            <UserAvatar name={msg.userName} size="md" />
          </div>
        ) : (
          <div className="w-9 shrink-0"></div>
        )}

        <div className="flex-1 min-w-0">
          {isFirstInGroup && (
            <div className="flex items-center gap-2 mb-0.5">
              <span className={`text-sm font-semibold ${memberRoleColors[msg.userRole]}`}>
                {msg.userName}
              </span>
              {msg.userRole !== 'listener' && <RoleBadge role={msg.userRole} />}
              <span className="text-[10px] text-foreground-500 ml-1">{msg.relativeTime}</span>
            </div>
          )}

          {/* Reply preview */}
          {msg.replyTo && msg.replyUserName && (
            <div className="flex items-center gap-1.5 mb-1 text-[11px]">
              <i className="ri-reply-line text-foreground-500 text-[10px]"></i>
              <span className="text-primary-400 font-medium">{msg.replyUserName}</span>
              <span className="text-foreground-500 truncate max-w-[200px]">{msg.replyPreview}</span>
            </div>
          )}

          {/* Attachment */}
          {msg.attachment && (
            <div className="mb-1.5 inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-background-800 border border-background-700 text-[11px]">
              <i className="ri-file-line text-foreground-400 text-xs"></i>
              <span className="text-foreground-300">{msg.attachment.name}</span>
              <span className="text-foreground-600">({msg.attachment.size})</span>
            </div>
          )}

          {/* Content */}
          <p className={`text-sm leading-relaxed ${memberRoleColors[msg.userRole] !== 'text-foreground-400' ? 'text-foreground-100' : 'text-foreground-200'}`}>
            {msg.content}
            {msg.edited && <span className="text-[9px] text-foreground-500 ml-1">(editado)</span>}
          </p>

          {/* Reactions */}
          <ReactionBar
            reactions={msg.reactions}
            onAddReaction={(emoji) => onReact(msg.id, emoji)}
            onRemoveReaction={(emoji) => onReact(msg.id, emoji)}
          />

          {/* Pinned / Highlighted labels */}
          <div className="flex items-center gap-2 mt-1">
            {isPinned && (
              <span className="text-[10px] text-primary-400 flex items-center gap-0.5">
                <i className="ri-pushpin-fill text-[9px]"></i> Fijado
              </span>
            )}
            {isHighlighted && !isPinned && (
              <span className="text-[10px] text-amber-400 flex items-center gap-0.5">
                <i className="ri-star-fill text-[9px]"></i> Destacado
              </span>
            )}
          </div>
        </div>

        {/* Timestamp hover */}
        {!isFirstInGroup && (
          <span className="text-[9px] text-foreground-600 opacity-0 group-hover:opacity-100 transition-opacity shrink-0 mt-0.5 whitespace-nowrap">
            {msg.relativeTime}
          </span>
        )}
      </div>

      {/* Hover actions */}
      {showActions && (
        <div className="absolute -top-3 right-4 z-30 flex items-center gap-0.5 bg-background-800 border border-background-700 rounded-lg p-0.5 shadow-lg">
          <button
            onClick={() => setShowReactionPicker(!showReactionPicker)}
            className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-background-700 text-foreground-400 hover:text-foreground-200 cursor-pointer transition-colors relative"
            title="Añadir reacción"
          >
            <i className="ri-emotion-happy-line text-sm"></i>
            {showReactionPicker && (
              <ReactionPicker
                onSelect={(emoji) => { onReact(msg.id, emoji); setShowReactionPicker(false); }}
                onClose={() => setShowReactionPicker(false)}
              />
            )}
          </button>
          <button
            onClick={() => onReply(msg)}
            className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-background-700 text-foreground-400 hover:text-foreground-200 cursor-pointer transition-colors"
            title="Responder"
          >
            <i className="ri-reply-line text-sm"></i>
          </button>
          <button
            onClick={() => onPin(msg)}
            className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-background-700 text-foreground-400 hover:text-foreground-200 cursor-pointer transition-colors"
            title={msg.isPinned ? 'Desfijar' : 'Fijar'}
          >
            <i className={`${msg.isPinned ? 'ri-pushpin-fill text-primary-400' : 'ri-pushpin-line'} text-sm`}></i>
          </button>
          <button
            onClick={() => onHighlight(msg)}
            className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-background-700 text-foreground-400 hover:text-foreground-200 cursor-pointer transition-colors"
            title={msg.isHighlighted ? 'Quitar destacado' : 'Destacar'}
          >
            <i className={`${msg.isHighlighted ? 'ri-star-fill text-amber-400' : 'ri-star-line'} text-sm`}></i>
          </button>
          <button
            onClick={() => onDelete(msg)}
            className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-red-500/20 text-foreground-400 hover:text-red-400 cursor-pointer transition-colors"
            title="Eliminar"
          >
            <i className="ri-delete-bin-line text-sm"></i>
          </button>
          <button
            onClick={() => onBlock(msg)}
            className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-red-500/20 text-foreground-400 hover:text-red-400 cursor-pointer transition-colors"
            title="Bloquear usuario"
          >
            <i className="ri-forbid-line text-sm"></i>
          </button>
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════
   PÁGINA PRINCIPAL
   ════════════════════════════════════════════ */

export default function AdminChatComunidadPage() {
  const [activeChannelId, setActiveChannelId] = useState(2);
  const [collapsedCategories, setCollapsedCategories] = useState<Set<number>>(new Set());
  const [messages, setMessages] = useState<ChatMessage[]>(() =>
    chatMessages.filter((m) => m.channelId === 2)
  );
  const [members, setMembers] = useState<ChatMember[]>(chatMembers);
  const [inputValue, setInputValue] = useState('');
  const [replyingTo, setReplyingTo] = useState<ChatMessage | null>(null);
  const [searchMember, setSearchMember] = useState('');
  const [isMuted, setIsMuted] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  /* ─── Scroll to bottom ─── */
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  /* ─── Switch channel ─── */
  const switchChannel = useCallback((channel: ChatChannel) => {
    setActiveChannelId(channel.id);
    setMessages(chatMessages.filter((m) => m.channelId === channel.id));
    setReplyingTo(null);
    setInputValue('');
  }, []);

  /* ─── Toggle category collapse ─── */
  const toggleCategory = useCallback((catId: number) => {
    setCollapsedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(catId)) next.delete(catId);
      else next.add(catId);
      return next;
    });
  }, []);

  /* ─── Active channel info ─── */
  const activeChannel = useMemo(() => {
    for (const cat of channelCategories) {
      const ch = cat.channels.find((c) => c.id === activeChannelId);
      if (ch) return ch;
    }
    return channelCategories[0].channels[0];
  }, [activeChannelId]);

  /* ─── Pinned messages ─── */
  const pinnedMessages = useMemo(() => messages.filter((m) => m.isPinned), [messages]);

  /* ─── Group messages by user ─── */
  const messageGroups = useMemo(() => {
    return messages.map((msg, idx) => {
      const prev = idx > 0 ? messages[idx - 1] : null;
      const isFirstInGroup = !prev || prev.userId !== msg.userId;
      return { msg, isFirstInGroup };
    });
  }, [messages]);

  /* ─── Handle file attach ─── */
  const handleAttachFile = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
    const sizeLabel = file.size > 1024 * 1024 ? `${sizeMB} MB` : `${(file.size / 1024).toFixed(0)} KB`;
    
    const attachmentInfo = `[Archivo: ${file.name} (${sizeLabel})]`;
    setInputValue((prev) => prev + (prev ? ' ' : '') + attachmentInfo);
    
    SwalAlert.toast(`Archivo "${file.name}" adjuntado al mensaje`, 'success');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  /* ─── Handle emoji insert ─── */
  const handleEmojiInsert = useCallback((emoji: string) => {
    setInputValue((prev) => prev + emoji);
    setShowEmojiPicker(false);
    inputRef.current?.focus();
  }, []);

  /* ─── Send message ─── */
  const handleSendMessage = useCallback(() => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;

    const newMsg: ChatMessage = {
      id: Math.max(...messages.map((m) => m.id), 200) + 1,
      channelId: activeChannelId,
      userId: 0,
      userName: 'Vos',
      userAvatar: '',
      userRole: 'admin',
      content: trimmed,
      timestamp: new Date().toISOString(),
      relativeTime: 'Ahora',
      reactions: [],
      replyTo: replyingTo?.id || null,
      replyPreview: replyingTo ? (replyingTo.content.length > 60 ? replyingTo.content.slice(0, 60) + '...' : replyingTo.content) : null,
      replyUserName: replyingTo?.userName || null,
      isPinned: false,
      isHighlighted: false,
      edited: false,
    };

    setMessages((prev) => [...prev, newMsg]);
    setInputValue('');
    setReplyingTo(null);
  }, [inputValue, activeChannelId, messages, replyingTo]);

  /* ─── Handle keydown ─── */
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  }, [handleSendMessage]);

  /* ─── Auto-resize textarea ─── */
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 120) + 'px';
  }, [inputValue]);

  /* ─── Actions ─── */
  const handleReply = useCallback((msg: ChatMessage) => {
    setReplyingTo(msg);
    inputRef.current?.focus();
  }, []);

  const handleReact = useCallback((msgId: number, emoji: ReactionEmoji) => {
    setMessages((prev) =>
      prev.map((m) => {
        if (m.id !== msgId) return m;
        const existing = m.reactions.find((r) => r.emoji === emoji);
        if (existing) {
          if (existing.reactedByMe) {
            const updated = m.reactions.map((r) =>
              r.emoji === emoji
                ? { ...r, count: r.count - 1, reactedByMe: false, users: r.users.filter((u) => u !== 'Vos') }
                : r
            ).filter((r) => r.count > 0);
            return { ...m, reactions: updated };
          }
          const updated = m.reactions.map((r) =>
            r.emoji === emoji
              ? { ...r, count: r.count + 1, reactedByMe: true, users: [...r.users, 'Vos'] }
              : r
          );
          return { ...m, reactions: updated };
        }
        return { ...m, reactions: [...m.reactions, { emoji, count: 1, users: ['Vos'], reactedByMe: true }] };
      })
    );
  }, []);

  const handlePin = useCallback(async (msg: ChatMessage) => {
    setMessages((prev) =>
      prev.map((m) => (m.id === msg.id ? { ...m, isPinned: !m.isPinned } : m))
    );
    await SwalAlert.toast(
      msg.isPinned ? 'Mensaje desfijado' : 'Mensaje fijado',
      msg.isPinned ? 'info' : 'success'
    );
  }, []);

  const handleHighlight = useCallback(async (msg: ChatMessage) => {
    setMessages((prev) =>
      prev.map((m) => (m.id === msg.id ? { ...m, isHighlighted: !m.isHighlighted } : m))
    );
    await SwalAlert.toast(
      msg.isHighlighted ? 'Destacado removido' : 'Mensaje destacado',
      msg.isHighlighted ? 'info' : 'success'
    );
  }, []);

  const handleDelete = useCallback(async (msg: ChatMessage) => {
    const result = await SwalAlert.confirm(
      '¿Eliminar mensaje?',
      `El mensaje de "${msg.userName}" será eliminado del chat.`,
      'Eliminar',
      'Cancelar',
      '#ef4444',
    );
    if (result.isConfirmed) {
      setMessages((prev) => prev.filter((m) => m.id !== msg.id));
      await SwalAlert.toast('Mensaje eliminado.', 'info');
    }
  }, []);

  const handleBlock = useCallback(async (msg: ChatMessage) => {
    const result = await SwalAlert.confirm(
      `¿Bloquear a ${msg.userName}?`,
      `El usuario será bloqueado del chat comunitario. No podrá enviar mensajes ni reaccionar.`,
      'Bloquear',
      'Cancelar',
      '#1f2937',
    );
    if (result.isConfirmed) {
      setMembers((prev) =>
        prev.map((m) => (m.id === msg.userId ? { ...m, isBlocked: true } : m))
      );
      setMessages((prev) => prev.filter((m) => m.userId !== msg.userId));
      await SwalAlert.toast(`${msg.userName} bloqueado del chat.`, 'info');
    }
  }, []);

  /* ─── Toggle mute ─── */
  const handleToggleMute = useCallback(async () => {
    setIsMuted((prev) => !prev);
    await SwalAlert.toast(
      isMuted ? 'Notificaciones de sonido activadas' : 'Notificaciones de sonido silenciadas',
      isMuted ? 'success' : 'info'
    );
  }, [isMuted]);

  /* ─── Online / offline counts ─── */
  const onlineCount = useMemo(() => members.filter((m) => m.isOnline && !m.isBlocked).length, [members]);
  const totalCount = useMemo(() => members.filter((m) => !m.isBlocked).length, [members]);

  /* ─── Filtered members ─── */
  const filteredMembers = useMemo(() => {
    if (!searchMember) return members.filter((m) => !m.isBlocked);
    return members.filter((m) =>
      !m.isBlocked &&
      (m.name.toLowerCase().includes(searchMember.toLowerCase()) ||
       m.username.toLowerCase().includes(searchMember.toLowerCase()))
    );
  }, [members, searchMember]);

  const onlineMembers = useMemo(() => filteredMembers.filter((m) => m.isOnline), [filteredMembers]);
  const offlineMembers = useMemo(() => filteredMembers.filter((m) => !m.isOnline), [filteredMembers]);

  return (
    <div className="h-screen bg-background-900 flex flex-col overflow-hidden">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        onChange={handleAttachFile}
        className="hidden"
        accept="image/*,.pdf,.doc,.docx,.txt,.zip"
      />

      {/* Mobile topbar */}
      <header className="lg:hidden flex items-center justify-between px-3 h-12 bg-background-950 border-b border-background-800 shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-md bg-primary-500 flex items-center justify-center">
            <i className="ri-chat-3-line text-background-50 text-[10px]"></i>
          </div>
          <span className="text-foreground-100 font-bold text-xs">Chat Comunidad</span>
        </div>
        <span className="text-emerald-400 text-[10px] font-semibold">{onlineCount} en línea</span>
      </header>

      <div className="flex flex-1 min-h-0">


        {/* ─── LEFT: Channel sidebar ─── */}
        <aside className="hidden lg:flex flex-col w-[220px] bg-background-950 border-r border-background-800 shrink-0">
          {/* Server header */}
          <div className="h-12 flex items-center px-4 border-b border-background-800 shrink-0">
            <h2 className="text-foreground-100 font-bold text-sm truncate">Radio Nueva Actitud</h2>
          </div>

          {/* Channel list */}
          <div className="flex-1 overflow-y-auto py-2 scrollbar-thin">
            {channelCategories.map((cat) => (
              <div key={cat.id} className="mb-1">
                <button
                  onClick={() => toggleCategory(cat.id)}
                  className="flex items-center gap-1 w-full px-4 py-1.5 text-[10px] font-bold text-foreground-500 uppercase tracking-wider hover:text-foreground-300 cursor-pointer transition-colors"
                >
                  <i className={collapsedCategories.has(cat.id) ? 'ri-arrow-right-s-line text-[8px]' : 'ri-arrow-down-s-line text-[8px]'}></i>
                  {cat.name}
                </button>
                {!collapsedCategories.has(cat.id) && cat.channels.map((ch) => (
                  <button
                    key={ch.id}
                    onClick={() => switchChannel(ch)}
                    className={`flex items-center gap-1.5 w-full px-4 py-1.5 text-sm cursor-pointer transition-colors group ${
                      activeChannelId === ch.id
                        ? 'bg-background-800/80 text-foreground-100'
                        : 'text-foreground-400 hover:text-foreground-200 hover:bg-background-800/40'
                    }`}
                  >
                    <i className={`text-sm ${ch.type === 'voice' ? 'ri-volume-up-line' : 'ri-hashtag'}`}></i>
                    <span className="truncate">{ch.name}</span>
                    {ch.unreadCount > 0 && activeChannelId !== ch.id && (
                      <span className="ml-auto bg-red-500 text-background-50 text-[9px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-1">
                        {ch.unreadCount}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            ))}
          </div>

          {/* User area */}
          <div className="h-13 flex items-center gap-2.5 px-3 bg-background-950/80 border-t border-background-800 shrink-0">
            <div className="relative shrink-0">
              <UserAvatar name="Carlos Herrera" size="sm" />
              <OnlineDot isOnline={true} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-foreground-100 font-semibold text-[11px] truncate">Carlos Herrera</p>
              <p className="text-foreground-500 text-[9px] truncate">@carlosherrera</p>
            </div>
            <div className="flex items-center gap-0.5">
              <button onClick={handleToggleMute} className={`w-6 h-6 flex items-center justify-center rounded-md cursor-pointer transition-colors ${isMuted ? 'text-red-400 bg-red-500/10' : 'text-foreground-500 hover:text-foreground-200 hover:bg-background-800'}`} title={isMuted ? 'Activar sonido' : 'Silenciar'}>
                <i className={`text-xs ${isMuted ? 'ri-volume-mute-line' : 'ri-volume-up-line'}`}></i>
              </button>
              <Link to="/admin/settings" className="w-6 h-6 flex items-center justify-center rounded-md text-foreground-500 hover:text-foreground-200 hover:bg-background-800 cursor-pointer transition-colors" title="Ajustes">
                <i className="ri-settings-3-line text-xs"></i>
              </Link>
            </div>
          </div>
        </aside>

        {/* ─── CENTER: Chat area ─── */}
        <main className="flex-1 flex flex-col min-w-0 bg-background-900">
          {/* Chat header */}
          <div className="h-12 flex items-center gap-2.5 px-4 border-b border-background-800 bg-background-900 shrink-0">
            <div className="flex items-center gap-2 min-w-0">
              <i className="ri-hashtag text-foreground-500 text-lg shrink-0"></i>
              <h3 className="text-foreground-100 font-semibold text-sm truncate">{activeChannel.name}</h3>
              {activeChannel.description && (
                <>
                  <span className="text-foreground-700 shrink-0">|</span>
                  <span className="text-foreground-500 text-xs truncate hidden sm:inline">{activeChannel.description}</span>
                </>
              )}
            </div>
            <div className="flex items-center gap-3 ml-auto shrink-0">
              <span className="text-foreground-500 text-[10px] hidden sm:inline">
                <i className="ri-user-3-line mr-0.5"></i>
                {onlineCount} en línea
              </span>
              <Link
                to="/"
                className="flex items-center gap-1 text-foreground-500 hover:text-foreground-300 text-[10px] transition-colors"
              >
                <i className="ri-external-link-line text-[9px]"></i>
                <span className="hidden sm:inline">Ver sitio</span>
              </Link>
            </div>
          </div>

          {/* Pinned messages bar */}
          {pinnedMessages.length > 0 && (
            <div className="px-4 py-1.5 bg-background-950/50 border-b border-background-800 flex items-center gap-1.5 shrink-0">
              <i className="ri-pushpin-fill text-primary-400 text-xs shrink-0"></i>
              <span className="text-primary-400 text-[10px] font-semibold shrink-0">{pinnedMessages.length} fijado{pinnedMessages.length > 1 ? 's' : ''}</span>
              <span className="text-foreground-600 text-[10px] truncate">
                — {pinnedMessages[0].userName}: {pinnedMessages[0].content.slice(0, 60)}...
              </span>
            </div>
          )}

          {/* Messages area */}
          <div className="flex-1 overflow-y-auto scrollbar-thin py-2">
            {messageGroups.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-foreground-600 gap-3">
                <i className="ri-chat-1-line text-4xl text-foreground-700"></i>
                <p className="text-sm">No hay mensajes en este canal todavía.</p>
                <p className="text-xs">¡Sé el primero en escribir algo!</p>
              </div>
            ) : (
              messageGroups.map(({ msg, isFirstInGroup }) => (
                <MessageBubble
                  key={msg.id}
                  msg={msg}
                  isFirstInGroup={isFirstInGroup}
                  onReply={handleReply}
                  onReact={handleReact}
                  onPin={handlePin}
                  onDelete={handleDelete}
                  onBlock={handleBlock}
                  onHighlight={handleHighlight}
                />
              ))
            )}
            <div ref={messagesEndRef}></div>
          </div>

          {/* Reply bar */}
          {replyingTo && (
            <div className="flex items-center gap-3 px-4 py-2 bg-background-800/80 border-t border-background-700 shrink-0">
              <i className="ri-reply-line text-foreground-500 text-sm shrink-0"></i>
              <div className="flex-1 min-w-0">
                <p className="text-primary-400 text-[11px] font-semibold">Respondiendo a {replyingTo.userName}</p>
                <p className="text-foreground-500 text-[10px] truncate">{replyingTo.content.slice(0, 80)}</p>
              </div>
              <button
                onClick={() => setReplyingTo(null)}
                className="w-6 h-6 flex items-center justify-center rounded-md text-foreground-500 hover:text-foreground-200 hover:bg-background-700 cursor-pointer transition-colors shrink-0"
              >
                <i className="ri-close-line text-sm"></i>
              </button>
            </div>
          )}

          {/* Input area */}
          <div className="px-4 pb-4 pt-2 shrink-0 bg-background-900">
            <div className="bg-background-800 rounded-xl border border-background-700 focus-within:border-background-600 transition-colors">
              <div className="flex items-end gap-2 p-2">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-500 hover:text-foreground-200 hover:bg-background-700 cursor-pointer transition-colors shrink-0"
                  title="Adjuntar archivo"
                >
                  <i className="ri-add-circle-line text-lg"></i>
                </button>
                <textarea
                  ref={inputRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={`Mensaje en #${activeChannel.name}`}
                  rows={1}
                  className="flex-1 bg-transparent border-none outline-none resize-none text-foreground-100 text-sm placeholder:text-foreground-600 py-1.5 min-h-[34px] max-h-[120px]"
                  maxLength={2000}
                />
                <div className="flex items-center gap-1 shrink-0">
                  <span className="text-foreground-600 text-[9px] hidden sm:inline">
                    {inputValue.length}/2000
                  </span>
                  <div className="relative">
                    <button
                      onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                      className="w-6 h-6 flex items-center justify-center rounded-lg text-foreground-500 hover:text-foreground-200 hover:bg-background-700 cursor-pointer transition-colors"
                      title="Emoji"
                    >
                      <i className="ri-emotion-line text-sm"></i>
                    </button>
                    {showEmojiPicker && (
                      <EmojiPickerPopover onSelect={handleEmojiInsert} onClose={() => setShowEmojiPicker(false)} />
                    )}
                  </div>
                  <button
                    onClick={handleSendMessage}
                    disabled={!inputValue.trim()}
                    className={`w-8 h-8 flex items-center justify-center rounded-lg cursor-pointer transition-all shrink-0 ${
                      inputValue.trim()
                        ? 'bg-primary-500 text-background-50 hover:bg-primary-600'
                        : 'bg-background-700 text-foreground-600 cursor-not-allowed'
                    }`}
                  >
                    <i className="ri-send-plane-fill text-sm"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </main>

        {/* ─── RIGHT: Member list ─── */}
        <aside className="hidden xl:flex flex-col w-[220px] bg-background-950 border-l border-background-800 shrink-0">
          <div className="h-12 flex items-center px-3 border-b border-background-800 shrink-0">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-foreground-600 text-xs"></i>
              <input
                type="text"
                value={searchMember}
                onChange={(e) => setSearchMember(e.target.value)}
                placeholder="Buscar miembro"
                className="w-full bg-background-800 border border-transparent focus:border-background-600 rounded-md pl-7 pr-2 py-1.5 text-foreground-200 text-[11px] outline-none placeholder:text-foreground-600 transition-colors"
              />
              {searchMember && (
                <button
                  onClick={() => setSearchMember('')}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 text-foreground-500 hover:text-foreground-300 cursor-pointer"
                >
                  <i className="ri-close-line text-[10px]"></i>
                </button>
              )}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto py-2 scrollbar-thin">
            {/* Online members */}
            {onlineMembers.length > 0 && (
              <div className="mb-2">
                <p className="px-3 py-1.5 text-[10px] font-bold text-foreground-500 uppercase tracking-wider">
                  EN LÍNEA — {onlineMembers.length}
                </p>
                {onlineMembers.map((member) => (
                  <div
                    key={member.id}
                    className="flex items-center gap-2 px-3 py-1.5 hover:bg-background-800/50 cursor-pointer transition-colors group"
                  >
                    <div className="relative shrink-0">
                      <UserAvatar name={member.name} size="sm" />
                      <OnlineDot isOnline={true} />
                    </div>
                    <div className="flex-1 min-w-0 flex items-center gap-1.5">
                      <span className={`text-xs font-medium truncate ${memberRoleColors[member.role]}`}>
                        {member.name}
                      </span>
                      {member.role !== 'listener' && <RoleBadge role={member.role} />}
                    </div>
                    {member.isTyping && (
                      <span className="text-emerald-400 text-[9px] italic shrink-0">escribiendo...</span>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Offline members */}
            {offlineMembers.length > 0 && (
              <div>
                <p className="px-3 py-1.5 text-[10px] font-bold text-foreground-600 uppercase tracking-wider">
                  DESCONECTADOS — {offlineMembers.length}
                </p>
                {offlineMembers.map((member) => (
                  <div
                    key={member.id}
                    className="flex items-center gap-2 px-3 py-1.5 hover:bg-background-800/50 cursor-pointer transition-colors group opacity-60"
                  >
                    <div className="relative shrink-0">
                      <UserAvatar name={member.name} size="sm" />
                      <OnlineDot isOnline={false} />
                    </div>
                    <span className="text-xs text-foreground-500 truncate">{member.name}</span>
                  </div>
                ))}
              </div>
            )}

            {filteredMembers.length === 0 && (
              <p className="text-foreground-600 text-[11px] text-center py-8">Sin resultados.</p>
            )}
          </div>
        </aside>
      </div>



      {/* Mobile channel switcher (bottom sheet trigger) */}
      <div className="lg:hidden fixed bottom-[56px] inset-x-0 z-40 px-3">
        <div className="bg-background-900/95 backdrop-blur-xl border border-background-800 rounded-t-xl px-3 py-2 flex items-center gap-2 overflow-x-auto">
          {channelCategories.flatMap((cat) => cat.channels).map((ch) => (
            <button
              key={ch.id}
              onClick={() => switchChannel(ch)}
              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[10px] font-semibold whitespace-nowrap cursor-pointer transition-all shrink-0 ${
                activeChannelId === ch.id
                  ? 'bg-background-700 text-foreground-100'
                  : 'bg-background-800 text-foreground-500 hover:text-foreground-300'
              }`}
            >
              <i className="ri-hashtag text-[9px]"></i>
              {ch.name}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}